import { useMemo, useState } from 'react';

import { navigate } from '@redwoodjs/router';
import { useMutation } from '@redwoodjs/web';

import Loader from 'src/components/Loader/Loader';
import ConfirmationModal from 'src/components/Modals/ConfirmationModal';
import { NPIDataGrid } from 'src/components/NPIDataGrid/NPIDataGrid';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { DELETE_CHECK_IN_CALL } from 'src/graphql/mutations/addCheckInCall.mutation';
import { formatDate } from 'src/utils/utils';
import { CheckInCallItem } from 'types/graphql';

import { CheckInCallSetupTableProps } from '../types';

import CheckInCallSetupGridColDef from './CheckInCallSetupGridColDef';

type SelectedItemType = {
    id: number;
    callName: string;
    callType: string;
    createdAt: string;
};

export const CheckInCallSetupTable = ({
    handleAddCheckInCall,
    loading,
    data,
    refetch,
}: CheckInCallSetupTableProps) => {
    const [isEditConfirmModalOpen, setIsEditConfirmModalOpen] = useState(false);

    const [isDeleteMode, setIsDeleteMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<(number | string)[]>([]);
    const [isBulkConfirmOpen, setIsBulkConfirmOpen] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    const [selectedItem, setSelectedItem] = useState<SelectedItemType | null>(null);

    const [deleteCheckInCall] = useMutation(DELETE_CHECK_IN_CALL);

    const handleEditCheckInCall = () => {
        if (!selectedItem) {
            showErrorToast('No item selected for editing.');
            return;
        }

        setIsEditConfirmModalOpen(false);

        setSelectedItem(null);

        navigate(`/config/check-in-call-view/${selectedItem.id}`);
    };

    const handleEdit = (item: CheckInCallItem) => {
        if (!isDeleteMode) {
            setIsEditConfirmModalOpen(true);
            setSelectedItem(item);
        }
    };

    const handleView = (rowId: string) => {
        if (!isDeleteMode) {
            navigate(`/config/check-in-call-view/${rowId}&view=true`);
        }
    };

    const exitDeleteMode = () => {
        setIsDeleteMode(false);
        setSelectedIds([]);
    };

    const enterDeleteMode = () => {
        setIsDeleteMode(true);
    };

    const openBulkConfirm = () => {
        if (selectedIds.length === 0) {
            return;
        }
        setIsBulkConfirmOpen(true);
    };

    const confirmBulkDelete = async () => {
        if (isBulkDeleting) {
            return;
        }
        setIsBulkDeleting(true);

        try {
            for (const id of selectedIds) {
                await deleteCheckInCall({ variables: { id: Number(id) } });
            }
            showSuccessToast(`Deleted ${selectedIds.length} check-in call(s)`);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_error) {
            showErrorToast('Error deleting selected check-in calls');
        } finally {
            setIsBulkDeleting(false);
            setIsBulkConfirmOpen(false);
            exitDeleteMode();
            refetch();
        }
    };

    const tableData = useMemo(() => {
        const result =
            [...data]?.map((checkIn) => {
                const { id, createdAt, callType, callName } = checkIn;
                return {
                    id,
                    callName,
                    callType,
                    createdAt: formatDate({ date: createdAt }),
                };
            }) ?? [];

        return {
            rows: result,
        };
    }, [data]);

    return (
        <>
            {loading || !data ? (
                <Loader />
            ) : (
                <>
                    <NPIDataGrid
                        rows={tableData.rows}
                        columns={CheckInCallSetupGridColDef(handleEdit)}
                        getRowId={(row) => {
                            return row.id;
                        }}
                        handleView={({ id }) => {
                            handleView(id);
                        }}
                        title="Check-in Call Setup"
                        addRecordButtonConfig={{
                            addRecordButtonText: 'Add Check-in Call',
                            addRecordCallBack: () => {
                                if (!isDeleteMode) {
                                    handleAddCheckInCall();
                                }
                            },
                        }}
                        dataTestId="check-in-call-setup-table"
                        onSelectionChange={(ids) => {
                            setSelectedIds(ids);
                        }}
                        selectedRowIds={selectedIds}
                        deleteModeConfig={{
                            isActive: isDeleteMode,
                            hasPermission: true,
                            onEnter: enterDeleteMode,
                            onCancel: exitDeleteMode,
                            onConfirm: openBulkConfirm,
                            disableConfirm: selectedIds.length === 0,
                        }}
                    />

                    <ConfirmationModal
                        isOpen={isBulkConfirmOpen}
                        onCancel={() => {
                            setIsBulkConfirmOpen(false);
                        }}
                        message={
                            `Are you sure you want to delete ${selectedIds.length} item${selectedIds.length !== 1 ? 's' : ''}?` +
                            '\n \n' +
                            `Please note that this will remove all the merchants listed under these check-in calls from the Customer Success/Check-in Calls page.`
                        }
                        title="Delete Check-in call"
                        onConfirm={confirmBulkDelete}
                        cancelText="No"
                        confirmText="Yes"
                        loading={isBulkDeleting}
                    />

                    <ConfirmationModal
                        isOpen={isEditConfirmModalOpen}
                        onCancel={() => {
                            setSelectedItem(null);
                            setIsEditConfirmModalOpen(false);
                        }}
                        message={
                            `Are you sure you want to edit ${selectedItem?.callName} Check-in Call?` +
                            '\n \n' +
                            `Please note that this will remove all the merchants listed under this ${selectedItem?.callName} from the Customer Success/Check-in Calls page.`
                        }
                        title="Edit Check-in call"
                        onConfirm={handleEditCheckInCall}
                    />
                </>
            )}
        </>
    );
};

export default CheckInCallSetupTable;
